import api from "@/services/api";
import type { User, UsersResponse, UpdateUserDto } from "@/types/User";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUserIdFromToken, getUserData, saveUserData } from "@/utils/jwt";

export const useGetCurrentUser = () => {
  const userId = getUserIdFromToken();
  const cachedUser = getUserData();

  return useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      // Se tem dados em cache, retorna eles
      if (cachedUser) {
        return cachedUser as User;
      }

      // Caso contrário, busca do servidor
      if (userId) {
        const response = await api.get<User>(`/users/${userId}`);
        return response.data;
      }

      throw new Error("Usuário não autenticado");
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  });
};

export const useGetUsers = (filters?: {
  page?: number;
  limit?: number;
  role?: string;
  isActive?: boolean;
}) => {
  return useQuery({
    queryKey: ["users", filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.limit) params.append("limit", filters.limit.toString());
      if (filters?.role) params.append("role", filters.role);
      if (filters?.isActive !== undefined)
        params.append("isActive", filters.isActive.toString());

      const response = await api.get<UsersResponse>(
        `/users?${params.toString()}`,
      );
      return response.data;
    },
  });
};

export const useGetUserById = (id: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async () => {
      const response = await api.get<User>(`/users/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateUser"],
    mutationFn: async (data: UpdateUserDto) => {
      const response = await api.patch<User>(`/users/me/profile`, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Atualizar localStorage com novos dados
      saveUserData(data);

      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      queryClient.invalidateQueries({ queryKey: ["user", data.id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deleteUser"],
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};
